import type { PageResponse } from '@mall/shared';
import { apiClient } from '../../app/api';
import type {
  AttributeInput,
  CreateProductRequest,
  ProductAttributeView,
  ProductSnapshot,
  UpdateProductRequest,
} from './types';

export interface ListParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
  categoryId?: number | null;
  brandId?: number | null;
  status?: number | null;
  priceMinCents?: number | null;
  priceMaxCents?: number | null;
  originalPriceMinCents?: number | null;
  originalPriceMaxCents?: number | null;
}

export interface BatchUpdatePricesRequest {
  ids: number[];
  priceCents?: number | null;
  originalPriceCents?: number | null;
}

export interface BatchUpdatePricesResponse {
  updated: number;
}

export interface BatchSortItem {
  id: number;
  sortOrder: number;
}

export interface BatchUpdateSortRequest {
  items: BatchSortItem[];
}

export function listProducts(params: ListParams = {}): Promise<PageResponse<ProductSnapshot>> {
  return apiClient.get<PageResponse<ProductSnapshot>>('/admin/products', {
    query: {
      page: params.page ?? 1,
      page_size: params.pageSize ?? 20,
      keyword: params.keyword || undefined,
      category_id: params.categoryId ?? undefined,
      brand_id: params.brandId ?? undefined,
      status: params.status ?? undefined,
      price_min_cents: params.priceMinCents ?? undefined,
      price_max_cents: params.priceMaxCents ?? undefined,
      original_price_min_cents: params.originalPriceMinCents ?? undefined,
      original_price_max_cents: params.originalPriceMaxCents ?? undefined,
    },
  });
}

export function exportProducts(params: ListParams = {}): Promise<Blob> {
  return apiClient.getBlob('/admin/products/export', {
    headers: {
      // Also accept JSON so that, on a server-side error, the JSON error body can be returned
      // (and surfaced to the user) instead of triggering a 406 "No acceptable representation".
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
    },
    query: {
      keyword: params.keyword || undefined,
      category_id: params.categoryId ?? undefined,
      brand_id: params.brandId ?? undefined,
      status: params.status ?? undefined,
      original_price_min_cents: params.originalPriceMinCents ?? undefined,
      original_price_max_cents: params.originalPriceMaxCents ?? undefined,
    },
  });
}

export function getProduct(id: number): Promise<ProductSnapshot> {
  return apiClient.get<ProductSnapshot>(`/admin/products/${id}`);
}

export function getProductAttributes(id: number): Promise<ProductAttributeView[]> {
  return apiClient.get<ProductAttributeView[]>(`/admin/products/${id}/attributes`);
}

export function createProduct(payload: CreateProductRequest): Promise<ProductSnapshot> {
  return apiClient.post<ProductSnapshot>('/admin/products', payload);
}

export function updateProduct(id: number, payload: UpdateProductRequest): Promise<ProductSnapshot> {
  return apiClient.put<ProductSnapshot>(`/admin/products/${id}`, payload);
}

export function updateProductPrice(
  id: number,
  originalPriceCents: number,
): Promise<ProductSnapshot> {
  return apiClient.patch<ProductSnapshot>(`/admin/products/${id}/price`, {
    originalPriceCents,
  });
}

export function updateProductImages(
  id: number,
  mainImage: string,
  images: string[],
): Promise<ProductSnapshot> {
  return apiClient.patch<ProductSnapshot>(`/admin/products/${id}/images`, {
    mainImage,
    images,
  });
}

export function batchUpdatePrices(
  payload: BatchUpdatePricesRequest,
): Promise<BatchUpdatePricesResponse> {
  return apiClient.patch<BatchUpdatePricesResponse>('/admin/products/batch-prices', payload);
}

export function batchUpdateSort(
  payload: BatchUpdateSortRequest,
): Promise<BatchUpdatePricesResponse> {
  return apiClient.patch<BatchUpdatePricesResponse>('/admin/products/batch-sort', payload);
}

export function publishProduct(id: number): Promise<void> {
  return apiClient.post<void>(`/admin/products/${id}/publish`);
}

export function unpublishProduct(id: number): Promise<void> {
  return apiClient.post<void>(`/admin/products/${id}/unpublish`);
}

export function deleteProduct(id: number): Promise<void> {
  return apiClient.delete<void>(`/admin/products/${id}`);
}

export function batchUnpublishProducts(ids: number[]): Promise<BatchUpdatePricesResponse> {
  return apiClient.post<BatchUpdatePricesResponse>('/admin/products/batch-unpublish', { ids });
}

export function batchPublishProducts(ids: number[]): Promise<BatchUpdatePricesResponse> {
  return apiClient.post<BatchUpdatePricesResponse>('/admin/products/batch-publish', { ids });
}

export function batchDeleteProducts(ids: number[]): Promise<BatchUpdatePricesResponse> {
  return apiClient.post<BatchUpdatePricesResponse>('/admin/products/batch-delete', { ids });
}

export interface BatchResetAttributesRequest {
  ids: number[];
  attributes: AttributeInput[];
}

export function batchResetProductAttributes(
  payload: BatchResetAttributesRequest,
): Promise<BatchUpdatePricesResponse> {
  return apiClient.post<BatchUpdatePricesResponse>(
    '/admin/products/batch-reset-attributes',
    payload,
  );
}
